
        <h4>What a business rule does</h4>
        <p>
            A business rule constrains which price buckets may be sold on the journeys
            you choose. It does not change the demand forecast or the capacity &mdash;
            it reshapes how the allocation is spread across buckets for the journeys
            and days that match.
        </p>
        <p>
            Rules are evaluated during the overnight run, or straight away if you choose
            to apply them when you save (see <strong>Applying rules now</strong> below).
            Wherever a rule has been applied, the Inventory workbench shows a green dot
            &mdash; or a red one where a rule that matched the journey lost the priority
            contest or had its cap nullified. Click either to see the rules involved.
        </p>

        <h4>The three parts of a rule</h4>
        <p>
            A rule is the row in this grid plus its <strong>Bucket Rules</strong> and
            <strong>Journey Rules</strong>. The Bucket Rules say <em>what</em> the rule
            does; the Journey Rules say <em>which journeys</em> it does it to. A journey
            has to match everything below before the rule takes effect.
        </p>
        <ul>
            <li><strong>Journey Type</strong> &mdash; the journey's type must be one of the ones you tick.</li>
            <li><strong>DOW</strong> &mdash; the days of the week the rule is active, based on the departure date.</li>
            <li><strong>Exclusion Dates</strong> &mdash; individual departure dates to skip.</li>
            <li><strong>Start Date / Expiration</strong> &mdash; the departure dates the rule covers, inclusive.</li>
            <li><strong>Draft</strong> &mdash; a draft rule is saved but never applied. Clear it to make the rule live.</li>
            <li><strong>Note</strong> &mdash; free text for your own reference; it has no effect on the rule.</li>
        </ul>
        <p>
            Both child grids sort on their column headers, which is the quickest way to
            read a long set of bucket rules in Class, Rule or Days Prior order. Sorting
            is display only and does not change how the rule is evaluated.
        </p>

        <h4>Bucket Rules &mdash; what the rule does</h4>
        <p>
            Each row is one passenger class, one rule type, one bucket and a Days Prior
            boundary. Remember that a higher bucket is a more expensive price point.
        </p>
        <ul>
            <li>
                <strong>Min</strong> &mdash; sets a floor. The bucket you name becomes the
                cheapest one available, and the allocation from every cheaper bucket moves
                up into it.
            </li>
            <li>
                <strong>Max</strong> &mdash; sets a ceiling. The bucket you name becomes the
                most expensive one available, and the allocation from every more expensive
                bucket moves down into it.
            </li>
            <li *ngIf="allocRulesVisible()">
                <strong>Min Alloc</strong> &mdash; guarantees a minimum in the named bucket,
                as a number of seats or as a percentage between 1 and 100 of the journey's
                total allocation: the seats already booked plus the seats allocated to
                sell, the Total Allocation column in the workbench, rounded up. It is a
                share of the allocation, not of the capacity, so on a thin flow a high
                percentage can still be a small number of seats. The unit toggle beside
                the value carries a number you have typed across when you switch it.
            </li>
            <li *ngIf="allocRulesVisible()">
                <strong>Max Alloc</strong> &mdash; caps the named bucket at this many seats and
                cascades what is left down the stack.
            </li>
        </ul>
        <p>
            <strong>Days Prior is a boundary, not a single day.</strong> Rows are expanded
            into ranges so that every day before departure is covered. If a Min rule has
            rows at 7 and 30 days prior, the 30 row applies from 30 days prior down to 8,
            and the 7 row applies from 7 days prior until departure. Max rows read in the
            opposite direction: the 7 row applies from 7 days prior out to 29, and the 30
            row applies from 30 days prior and earlier.
        </p>

        <h4>Journey Rules &mdash; which journeys it applies to</h4>
        <ul>
            <li>
                <strong>Origin / Destination</strong> &mdash; the journey being sold. Choose
                <code>*ALL</code> to match any station.
            </li>
            <li>
                <strong>Services/Markets</strong> &mdash; leave empty to match every service.
                Otherwise name specific services, or a market to match every service in it.
                A service that does not appear in the list yet can be typed in and entered
                anyway; it is stored in upper case, which is what the rules engine matches
                on.
            </li>
        </ul>

        <h4>Priority &mdash; when more than one rule matches</h4>
        <p>
            A journey can only take one Min rule and one Max rule<span *ngIf="allocRulesVisible()">
            (and one of each Alloc rule per bucket)</span>. The most specific match wins:
        </p>
        <ol class="priority-list">
            <li>Service + both stations</li>
            <li>Service + one <code>*ALL</code></li>
            <li>Service + <code>*ALL</code>/<code>*ALL</code></li>
            <li>Market + both stations</li>
            <li>Market + one <code>*ALL</code></li>
            <li>Market + <code>*ALL</code>/<code>*ALL</code></li>
            <li>No service + both stations</li>
            <li>No service + one <code>*ALL</code></li>
            <li>No service + <code>*ALL</code>/<code>*ALL</code></li>
        </ol>
        <p>
            If two rules are still tied, the one with the later Start Date wins, then the
            first by name. <strong>Prioritize</strong> lets you override the winner for a
            particular journey, and <strong>Send Conflict Report</strong> produces a report
            of rules that overlap.
        </p>

        <div *ngIf="volumeTriggerVisible()">
            <h4>Volume Trigger &mdash; only apply when the train is busy (or quiet)</h4>
            <p>
                A volume trigger makes the rule conditional on how full the train already
                is. Leave it set to <strong>None</strong> and the rule always applies.
            </p>
            <ul>
                <li>
                    <strong>Underconstrained</strong> / <strong>Overconstrained</strong> &mdash;
                    compare the load factor (bookings &divide; capacity) of the journey's most
                    constrained leg against the Volume Factor, entered as a percentage.
                </li>
                <li>
                    <strong>Underselling</strong> / <strong>Overselling</strong> &mdash; compare
                    the bookings on the journey's busiest leg against the Volume Factor,
                    entered as a number of seats.
                </li>
            </ul>
            <p>
                A leg's bookings count <em>everyone</em> travelling over that leg, including
                flex fares &mdash; not just the passengers making the exact journey the rule
                targets. So a leg total is normally much larger than the journey's own
                bookings.
            </p>
            <p>
                By default the system picks the leg for you. To pin the rule to a particular
                leg, set <strong>Trigger Leg Origin</strong> and <strong>Trigger Leg
                Destination</strong> &mdash; you must set both or neither. A leg is a single
                hop between two consecutive stops, so if a train does not run over that leg
                the rule will not apply to it at all.
            </p>
        </div>

        <h4>Assess Impact &mdash; what a rule would do before you save it</h4>
        <p>
            The chart icon on a row, and the <strong>Assess Impact</strong> button in the
            toolbar, open an assessment of what that rule would change if it went live at
            the next overnight run. It is read-only: nothing is saved by assessing.
        </p>
        <p>
            <strong>It is only offered for rules the engine has not applied</strong> &mdash;
            a new row, a clone, a draft, or a rule with unsaved edits staged in the grid.
            A live rule already shapes today's recommendations, so assessing it would
            mostly compare the book against itself; the icon is disabled and says so.
        </p>
        <ul>
            <li>
                The strip under the title names the scope assessed and how many journeys
                were evaluated. <strong>Journeys Governed</strong> is how many of them the
                rule would actually win on, and <strong>Governed Today</strong> separates
                those it would reach now from those it only reaches as the days-prior bands
                come into range.
            </li>
            <li>
                <strong>&Delta; Revenue &middot; no demand change</strong> is the headline:
                the fare change applied to the revenue still forecast to be sold on the
                journeys the rule governs. It assumes the same passengers still travel.
            </li>
            <li>
                A rule that combines several rule types gets one tab per layer, plus
                <strong>Overall</strong>. The tabs are applied in engine order and sum to
                the Overall, unless a note says some layers were skipped.
            </li>
            <li>
                <strong>Revenue Ballpark</strong> shows the arithmetic in the open: demand
                still to be sold, the revenue base at today's fare, and the change to the
                average price across the open ladder. On a sub-rule tab it is followed by a
                what-if table putting a figure on various levels of demand loss, and a
                break-even line. How much demand you would lose is a judgement the tool
                does not make for you.
            </li>
            <li>
                For an allocation rule that moves seats between buckets without changing
                the fares on sale, the estimate re-prices the same planned volume at the
                new mix and says so. A demand response to that is not modelled.
            </li>
            <li>
                Conflicts with other rules are listed and counted, but they do not block
                anything &mdash; priority order still decides the winner. Rules matching
                <code>*ALL</code> are not conflict-checked, and the dialog says when that
                applies.
            </li>
        </ul>
        <p>
            Where the rule cannot be assessed as it stands, the dialog says why rather
            than showing nothing: it runs past the end of the evaluated horizon, its scope
            is too broad to price interactively, or it would govern no journeys at all.
            The first two offer a <strong>Narrow the assessment scope</strong> card &mdash;
            dates, markets and class &mdash; and an <strong>Assess Narrowed Scope</strong>
            button.
        </p>
        <p>
            <strong>Export to Excel</strong> writes out the journeys behind the numbers.
            Where a rule has staged edits, <strong>Save Rule</strong> saves it without
            leaving the dialog.
        </p>
        <p>
            Every figure comes from the last overnight optimisation and the fares as of
            that run, both dated in the dialog. Expect small differences against the live
            reservation system.
        </p>

        <h4>Change History</h4>
        <p>
            The history icon on a row opens a read-only record of every saved change to
            that rule and to its bucket, journey, day-of-week and journey-type children,
            newest first: who changed it, when, and each field before and after.
        </p>
        <ul>
            <li>A rule that has never been saved &mdash; a new row, or a clone &mdash; has no history, and the icon is disabled.</li>
            <li>Edits you have staged in the grid but not saved are not in it. A note at the top says so when that is the case.</li>
            <li>History is not kept forever. Where older entries have been purged the dialog says the earlier detail is no longer retained rather than implying the change never happened.</li>
        </ul>

        <h4>Saving your changes</h4>
        <p>
            <strong>Save</strong> commits every change on the screen, including the Bucket
            and Journey Rules. <strong>Clone</strong> copies the rules you have ticked, and
            <strong>Export</strong> downloads the current rules as a CSV. Rule names are
            stored without leading or trailing spaces.
        </p>
        <p>
            Saving reports back rule by rule: how many saved, and for anything that
            failed, the rule name and the reason. <strong>Rules are saved
            individually</strong>, so a summary listing both saved and failed rules means
            exactly that &mdash; the successful ones are committed and only the failures
            need re-doing.
        </p>
        <p>
            The usual reason for a failure is that <strong>someone else changed the same
            rule since your screen loaded</strong>. That rule is rejected rather than
            silently overwriting their work, and the summary names who changed it last.
            Reload the screen to pick up their version before re-applying your change.
        </p>

        <h4>Applying rules now</h4>
        <p>
            After a save, a second dialog, <strong>Apply Business Rules Now</strong>, offers
            to apply the rules you just saved to today's inventory instead of waiting for
            tonight's run. Tick the rules to apply and choose the departure dates; an
            estimate of the journeys, ODs, dates and buckets affected updates as you
            change them. An immediate apply covers at most 31 departure dates &mdash; the
            rest of the range applies in tonight's run as usual.
        </p>
        <ul>
            <li>
                <strong>Skip</strong> keeps the saved rules for tonight's run.
                <strong>Discard</strong> undoes the save as well.
            </li>
            <li>
                <strong>Apply and Review</strong> recomputes the journeys the rules govern
                and stages the new limits for you to check. The <strong>Review &amp;
                Send</strong> button in this screen's toolbar then shows a count and opens
                the same review dialog as the Inventory workbench, where you can untick
                journeys before sending.
            </li>
            <li>
                <strong>Apply and Send</strong> does the same and sends the result straight
                to RARS.
            </li>
        </ul>
        <p>
            Only dates the rules actually govern change anything. A rule that starts in
            November, applied over an October window, lands nothing &mdash; and there is
            then nothing to review. A rule covering a whole market can take a few minutes
            per governed date; progress and the outcome arrive in the notification bell.
        </p>
